import { useCallback } from 'react';

import {
  LandlordFormState,
  LandlordStepType,
  LandlordFormAnswer,
  LandlordApiResponses,
  LandlordQuoteSubSection,
  LandlordSubSection,
  LandlordPolicySubSection,
  LandlordCoverSubSection,
} from 'scenes/landlord/types';

import {
  nextPage,
  previousPage,
  restartQuote,
  nextIfLocationIs,
} from 'scenes/common/actions';
import {
  apiResponse,
  editCoverDetails,
  editPolicyDetails,
  editQuoteDetails,
} from 'scenes/landlord/actions';

const toStepType = (
  landlordSubSection: LandlordSubSection | undefined
): LandlordStepType => {
  switch (landlordSubSection) {
    case LandlordQuoteSubSection.PolicyStartDate:
      return LandlordStepType.PolicyStartDate;
    case LandlordQuoteSubSection.MainAddress:
      return LandlordStepType.MainAddress;
    case LandlordQuoteSubSection.PropertyOwnership:
      return LandlordStepType.Ownership;
    case LandlordQuoteSubSection.PropertyPurpose:
      return LandlordStepType.PropertyPurpose;
    case LandlordQuoteSubSection.RentalAgreement:
      return LandlordStepType.RentalAgreement;
    case LandlordQuoteSubSection.WeeklyRent:
      return LandlordStepType.WeeklyRentPaid;
    case LandlordQuoteSubSection.UsedForBusiness:
      return LandlordStepType.PropertyUsedForBusiness;
    case LandlordQuoteSubSection.IssuedToPersonOrBusiness:
      return LandlordStepType.PersonalOrBusinessPolicy;
    case LandlordQuoteSubSection.ContactDateOfBirth:
      return LandlordStepType.ContactDateOfBirth;
    case LandlordQuoteSubSection.BuildingType:
      return LandlordStepType.BuildingType;
    case LandlordQuoteSubSection.RecentInsurer:
      return LandlordStepType.RecentInsurer;
    case LandlordQuoteSubSection.YearsInsured:
      return LandlordStepType.YearsInsured;
    case LandlordQuoteSubSection.ClaimsMade:
      return LandlordStepType.ClaimsMade;
    case LandlordQuoteSubSection.BreakIn:
      return LandlordStepType.BreakInAndAdditionalSecurity;
    case LandlordQuoteSubSection.AdditionalSecurity:
      return LandlordStepType.BreakInAndAdditionalSecurity;
    case LandlordQuoteSubSection.NumberOfBedrooms:
      return LandlordStepType.NumberOfBedrooms;
    case LandlordQuoteSubSection.PropertyBuiltYear:
      return LandlordStepType.PropertyBuiltYear;
    case LandlordQuoteSubSection.CertificateOfOccupancy:
      return LandlordStepType.PropertyBuiltYear;
    case LandlordQuoteSubSection.RoofMaterial:
      return LandlordStepType.RoofMaterial;
    case LandlordQuoteSubSection.WallMaterial:
      return LandlordStepType.WallMaterial;
    case LandlordQuoteSubSection.SecurityFeatures:
      return LandlordStepType.SecurityFeatures;
    case LandlordQuoteSubSection.BuildingSumInsuredChoice:
      return LandlordStepType.SumInsuredChoice;
    case LandlordQuoteSubSection.BuildingAgreedSumInsured:
      return LandlordStepType.SumInsuredBuildingValue;
    case LandlordQuoteSubSection.ContentsSumInsured:
      return LandlordStepType.SumInsuredContentsValue;
    case LandlordQuoteSubSection.FlybuysNumber:
      return LandlordStepType.FlybuysNumber;
    case LandlordPolicySubSection.PersonalPolicyContact:
      return LandlordStepType.PersonalPolicyContact;
    case LandlordPolicySubSection.BusinessPolicyContact:
      return LandlordStepType.BusinessContact;
    case LandlordPolicySubSection.ContactPhoneNumber:
      return LandlordStepType.ContactPhoneNumber;
    case LandlordPolicySubSection.ContactEmailAddress:
      return LandlordStepType.ContactEmailAddress;
    case LandlordPolicySubSection.PostalAddress:
      return LandlordStepType.PostalAddress;
    case LandlordPolicySubSection.PolicyHolderDeclaration:
      return LandlordStepType.PolicyHoldersHistory;
    case LandlordPolicySubSection.BusinessNameAndAbn:
      return LandlordStepType.BusinessNameAndAbn;
    case LandlordCoverSubSection.BuildingExcess:
      return LandlordStepType.BuildingExcess;
    case LandlordCoverSubSection.ContentsExcess:
      return LandlordStepType.ContentsExcess;
    case LandlordCoverSubSection.OptionalExtras:
      return LandlordStepType.OptionalExtras;
    case LandlordPolicySubSection.ProperyInGoodCondition:
      return LandlordStepType.PropertyCondition;
    case LandlordPolicySubSection.PropertyUnderRenovation:
      return LandlordStepType.PropertyRenovation;
    case LandlordPolicySubSection.HeritageListed:
      return LandlordStepType.HeritageListed;
    case LandlordPolicySubSection.ReplumbedOrRewired:
      return LandlordStepType.RewiredOrReplumbed;
    case LandlordPolicySubSection.PropertyUnoccupied:
      return LandlordStepType.PropertyUnoccupied;
    case LandlordPolicySubSection.PropertyOver5Acres:
      return LandlordStepType.Over5Acres;
    case LandlordPolicySubSection.PropertyUnrelatedPeople:
      return LandlordStepType.PropertyOccupancy;
    case LandlordPolicySubSection.AdditionalPolicyHolders:
      return LandlordStepType.AdditionalPolicyHolderList;
    case LandlordPolicySubSection.Mortgage:
      return LandlordStepType.MortgageProvider;
    default:
      return LandlordStepType.PolicyStartDate;
  }
};

export const useFlowActions = (
  handleAnswer: (f: LandlordFormAnswer) => void
) => {
  const onNext = useCallback(
    (values: LandlordFormState) => {
      handleAnswer(nextPage(values));
    },
    [handleAnswer]
  );

  const onNextWithReset = useCallback(
    (values: LandlordFormState, resetFields: LandlordFormState) => {
      const formValuesWithResets = { ...values, ...resetFields };
      handleAnswer(nextPage(formValuesWithResets));
    },
    [handleAnswer]
  );

  const onNextIfLocationIs = useCallback(
    (step: LandlordStepType, values: LandlordFormState = {}) => {
      handleAnswer(nextIfLocationIs(step, values));
    },
    [handleAnswer]
  );

  const onRestartQuote = useCallback(() => {
    handleAnswer(restartQuote());
  }, [handleAnswer]);

  const onApiResponse = useCallback(
    (response: Partial<LandlordApiResponses>) => {
      handleAnswer(apiResponse(response));
    },
    [handleAnswer]
  );

  const onPrevious = useCallback(() => {
    handleAnswer(previousPage());
  }, [handleAnswer]);

  const onEditQuoteDetails = useCallback(
    (homeSubSection: LandlordQuoteSubSection) => {
      handleAnswer(editQuoteDetails(toStepType(homeSubSection)));
    },
    [handleAnswer]
  );

  const onEditPolicyDetails = useCallback(
    (homeSubSection: LandlordPolicySubSection) => {
      handleAnswer(editPolicyDetails(toStepType(homeSubSection)));
    },
    [handleAnswer]
  );

  const onEditCoverDetails = useCallback(
    (homeSubSection: LandlordCoverSubSection) => {
      handleAnswer(editCoverDetails(toStepType(homeSubSection)));
    },
    [handleAnswer]
  );

  return {
    onNext,
    onPrevious,
    onApiResponse,
    onRestartQuote,
    onNextIfLocationIs,
    onEditQuoteDetails,
    onEditCoverDetails,
    onEditPolicyDetails,
    onNextWithReset,
  };
};

export default useFlowActions;
